/**
 * waveScheduler.ts
 * Time-driven enemy wave scheduler for the World 2 space scene.
 *
 * Each level may declare a `waveScript` — an ordered, immutable list of
 * `WaveSpec` entries with an `atSeconds` trigger time. While the script
 * is active, `updateWaveScheduler` checks `spaceState.elapsedSeconds`
 * each tick and fires any wave whose trigger has passed. A single
 * module-local index tracks how far through the script we are, so even
 * if a tick is fat (e.g. tab-resumed) and skips multiple wave thresholds
 * at once, every wave still fires in order.
 *
 * Formations:
 *  - `'line'`  — vertical column. All enemies share the spawn X (right
 *    edge of the viewport); each takes a different Y offset from
 *    `baseY` with `spacing` pixels between slots.
 *  - `'v'`     — V pointed at the player. Lead enemy at front-centre
 *    (smallest X), trailing pairs offset symmetrically up/down and
 *    back-right so they appear to follow the lead.
 *  - `'arc'`   — vertical arc. Same column shape as `'line'`, but each
 *    slot bows slightly to the right (off-screen) so the centre enemies
 *    enter the viewport first and the outer enemies trail in.
 *
 * Spawning reuses the shared `SPAWN_SPEC` pool acquire path from
 * `spaceEnemySystem` so pool-cap + per-kind vertical clamping behave
 * identically to the Poisson spawner. A wave slot whose pool is
 * exhausted is dropped silently (same contract as the Poisson path).
 *
 * NO React, NO Reanimated, NO async — engine module.
 *
 * Phase 4 — World 2: Starfield Drift (Level 2-2 wave gameplay).
 *
 * @module waveScheduler
 */

import { spaceState } from './spaceState';
import { SPAWN_SPEC } from './spaceEnemyPools';
import type { SpaceEnemyKind } from '../../constants/spaceEnemyConstants';
import {
  SPACE_ENEMY_SPAWN_X_OFFSET,
  SPACE_ENEMY_SPAWN_Y_MIN,
  SPACE_ENEMY_SPAWN_Y_MAX_OFFSET,
} from '../../constants/spaceEnemyConstants';
import {
  VIEWPORT_WIDTH,
  VIEWPORT_HEIGHT,
} from '../../constants/gameConstants';

/** Formation shape applied to a single wave's enemies. */
export type WaveFormation = 'line' | 'v' | 'arc';

/**
 * One scheduled wave inside a level's `waveScript`.
 *
 * @property atSeconds  - Trigger time, relative to scene start (matches
 *                        `spaceState.elapsedSeconds`).
 * @property kind       - Which enemy kind to spawn for every slot in
 *                        the formation. All slots in a wave share one
 *                        kind for now (mixed-kind formations are a
 *                        future enhancement).
 * @property formation  - One of `'line' | 'v' | 'arc'`.
 * @property count      - How many enemies (1+). Capped per-call at the
 *                        per-kind pool size; the spawner drops any
 *                        slots whose pool is exhausted.
 * @property baseY      - Optional fixed centre Y for the formation. If
 *                        omitted, the scheduler uses the viewport's
 *                        vertical midpoint.
 * @property spacing    - Optional pixel spacing between formation slots.
 *                        Default 24px (matches the FighterDrone height).
 */
export interface WaveSpec {
  readonly atSeconds: number;
  readonly kind: SpaceEnemyKind;
  readonly formation: WaveFormation;
  readonly count: number;
  readonly baseY?: number;
  readonly spacing?: number;
}

const DEFAULT_SPACING_PX = 24;

/**
 * `'v'` formation tail offset, in pixels. The lead enemy (slot 0) spawns
 * at the wave's base X; each trailing pair adds this many pixels of
 * additional right-offset, so the V "drags" off the right edge of the
 * viewport and walks in over the next ~0.5s of auto-scroll.
 */
const V_TAIL_X_STEP_PX = 28;

/**
 * `'arc'` formation outer-slot offset, in pixels. The slot at the centre
 * of the arc spawns at the wave's base X; outer slots add up to this many
 * extra pixels of right-offset, so the centre enemies enter the viewport
 * first.
 */
const ARC_MAX_X_OFFSET_PX = 24;

/**
 * Module-local cursor into the active wave script. Reset between scene
 * lifetimes via `resetWaveScheduler()`. Mirrors the
 * `nextWaveIndex` pattern used by other one-shot triggers (e.g. boss
 * trigger flag inside `bossSystem`).
 *
 * DECISION: A single monotonic index is sufficient because waveScripts
 * are sorted by `atSeconds` at module load (validated by the test) — the
 * scheduler never needs random access into the list.
 */
let nextWaveIndex = 0;

/**
 * Reset the wave-script cursor. Called from `SpaceScene.init()` so each
 * level lifetime starts at slot 0 of its waveScript.
 */
export function resetWaveScheduler(): void {
  nextWaveIndex = 0;
}

/**
 * Read the current wave index. Test-only seam.
 *
 * @internal
 */
export function getNextWaveIndexForTest(): number {
  return nextWaveIndex;
}

/**
 * Compute per-slot (xOffset, yOffset) pairs for a formation. The result
 * is consumed by `spawnWave` and is exported separately so the formation
 * geometry can be tested without touching pools or `spaceState`.
 *
 * @param formation - Formation shape.
 * @param count     - Slot count (1+).
 * @param spacing   - Pixel spacing between slots; defaults to 24.
 * @returns Array of `{ xOffset, yOffset }` in formation order.
 */
export function computeFormationOffsets(
  formation: WaveFormation,
  count: number,
  spacing: number = DEFAULT_SPACING_PX,
): ReadonlyArray<{ xOffset: number; yOffset: number }> {
  if (count < 1) return [];

  // For each formation, slot 0 is the "lead" — the slot closest to the
  // left edge of the viewport (and so the first to enter). All offsets
  // are RELATIVE to the wave's base (X, Y); positive xOffset trails
  // further off-screen to the right.
  const out: { xOffset: number; yOffset: number }[] = [];

  if (formation === 'line') {
    // Vertical column. All slots at xOffset = 0. Y spreads symmetrically
    // around 0: count=3 → -spacing, 0, +spacing.
    const mid = (count - 1) / 2;
    for (let i = 0; i < count; i++) {
      out.push({ xOffset: 0, yOffset: Math.round((i - mid) * spacing) });
    }
    return out;
  }

  if (formation === 'v') {
    // Slot 0 = lead at (0, 0). Subsequent slots pair up/down with
    // increasing right-trail offset. count=5 → slots: lead, +1y/-1y at
    // step1, +2y/-2y at step2.
    out.push({ xOffset: 0, yOffset: 0 });
    let row = 1;
    for (let i = 1; i < count; i += 2) {
      const xTrail = row * V_TAIL_X_STEP_PX;
      out.push({ xOffset: xTrail, yOffset: -Math.round(row * spacing) });
      if (i + 1 < count) {
        out.push({ xOffset: xTrail, yOffset: Math.round(row * spacing) });
      }
      row++;
    }
    return out;
  }

  // 'arc' — vertical column with a quadratic right-bow so the outer
  // slots trail the centre. xOffset(i) = ARC_MAX_X_OFFSET_PX * (2*t - 1)^2
  // where t in [0, 1] maps slot index to a normalised position. Slot at
  // the centre of the column (t = 0.5) gets xOffset = 0; the top/bottom
  // slots get xOffset = ARC_MAX_X_OFFSET_PX.
  const mid = (count - 1) / 2;
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : i / (count - 1);
    const arc = (2 * t - 1) * (2 * t - 1);
    out.push({
      xOffset: Math.round(ARC_MAX_X_OFFSET_PX * arc),
      yOffset: Math.round((i - mid) * spacing),
    });
  }
  return out;
}

/**
 * Spawn one wave's enemies into `spaceState.enemies`. Slots whose pool
 * is exhausted are dropped silently (same contract as the Poisson
 * spawner in `spaceEnemySystem`). Y values are clamped to the viewport's
 * usable spawn band so a tall formation never spawns half off-screen.
 *
 * @param spec - Wave specification.
 */
function spawnWave(spec: WaveSpec): void {
  const pool = SPAWN_SPEC[spec.kind];
  const baseX = spaceState.cameraX + VIEWPORT_WIDTH + SPACE_ENEMY_SPAWN_X_OFFSET;
  const baseY = spec.baseY ?? Math.floor(VIEWPORT_HEIGHT / 2);
  const spacing = spec.spacing ?? DEFAULT_SPACING_PX;
  const offsets = computeFormationOffsets(spec.formation, spec.count, spacing);

  const yMin = SPACE_ENEMY_SPAWN_Y_MIN + pool.yMinInset;
  const yMax = VIEWPORT_HEIGHT - pool.verticalReach - SPACE_ENEMY_SPAWN_Y_MAX_OFFSET;

  for (const { xOffset, yOffset } of offsets) {
    const drone = pool.acquire();
    if (drone === null) continue; // Pool exhausted — match Poisson contract.
    const x = baseX + xOffset;
    const y = Math.max(yMin, Math.min(yMax, baseY + yOffset));
    drone.spawn(x, y);
    spaceState.enemies.push(drone);
  }
}

/**
 * Fire any waves whose `atSeconds` has been crossed by
 * `spaceState.elapsedSeconds`. Idempotent across ticks: the cursor only
 * advances when a wave actually fires, and a stale `dt` parameter is
 * intentionally accepted-but-unused so callers don't have to special-case
 * the wave-update slot when wiring this into `stepSpace`.
 *
 * @param _dt   - Fixed timestep in seconds. Unused — the scheduler reads
 *                `spaceState.elapsedSeconds` directly.
 * @param script - The active level's wave script, sorted by `atSeconds`.
 */
export function updateWaveScheduler(
  _dt: number,
  script: ReadonlyArray<WaveSpec>,
): void {
  while (
    nextWaveIndex < script.length &&
    script[nextWaveIndex].atSeconds <= spaceState.elapsedSeconds
  ) {
    spawnWave(script[nextWaveIndex]);
    nextWaveIndex++;
  }
}
